import {
  ParlorHeadSection,
  ATypeMainHead,
  ATypeCardView,
  ATypeParlorList,
  ATypeFlowerSummary,
  excludeParlorList,
  ATypeButtonLinks,
  HLine,
  colors,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";
import parlors from "goi_common/src/constants/parlors_detail.json";

import React from "react";
import { ParlorDetailProps, DTypeTabs } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";
import { carouselRichSearch, regionParlors } from "@/constants/richSearch";
import { BreadCrumb } from "@/components/BreadCrumb";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = parlors
    .filter((res) => targetSidos.includes(res.sido))
    .map((parlor) => ({
      params: {
        id: String(parlor.id),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const id = params?.id;
  let parlor;
  let district;
  let name = "";
  let description = "";

  if (id) {
    parlor = parlors.find((parlor) => parlor.id === Number(id));
    if (parlor) {
      name = parlor.companyname;

      district = parlor.address.slice(0, 2);
      if (parlor.address.split(" ")[0].length == 4)
        district = parlor.address[0] + parlor.address[2];

      const descParts = [
        `${parlor.companyname} 근조화환·조화 배송 안내입니다`,
        `고이장례연구소에서 e하늘 장사정보 기반으로 안내드립니다`,
        `주소 : ${parlor.address}`,
        `2시간 이내 당일 배송`,
        `삼가 고인의 명복을 빕니다`,
      ].filter(Boolean);
      description = descParts.join(' | ');
    }
  }

  return {
    props: {
      defaultParlor: parlor,
      pageDescription: description,
      pageKeywords: `${parlor?.companyname}, ${parlor?.companyname} 화환, ${parlor?.companyname} 화환 배송, ${parlor?.companyname} 근조화환, ${parlor?.companyname} 꽃, ${parlor?.companyname} 꽃배달, ${parlor?.companyname} 꽃집, 꽃배달, 꽃, 꽃집, 화환`,
      pageOgTitle: `${parlor?.companyname} 근조화환 배송 안내 (${district}) | 대구경북 장례정보 [고이장례연구소]`,
      pageOgDescription: description,
      pageOgImage: parlor?.images[0]?.url || "",
      hasError: !parlor,
    },
  };
};

interface FlowerProps {
  defaultParlor: ParlorDetailProps;
  pageOgTitle: string;
}

export default function Flower({ defaultParlor, pageOgTitle }: FlowerProps) {
  return (
    <>
      <Head>
        <title>{pageOgTitle}</title>
        {regionParlors.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>

      <ParlorHeadSection
        component={
          <>
            <BreadCrumb
              defaultParlor={defaultParlor}
              event={{ path: "funeral_place_flower" }}
            />
            <ATypeMainHead
              defaultParlor={defaultParlor}
              title={`${defaultParlor.companyname} 근조화환`}
              managed_type={defaultParlor.managed_type}
            />
          </>
        }
      />
      
      <Box padding="0 20px">
        <HLine height="1px" color={colors.gray900} />
      </Box>
      <Box width="100%" className="subtitle_14_sb gray900" padding="0 20px" margin="12px 0" textAlign="center">
        근조화환 배송 서비스 (고이장례연구소)
      </Box>
      <Box padding="0 20px" marginBottom="32px">
        <HLine height="1px" color={colors.gray900} />
      </Box>

      <ParlorFlowerContentSection defaultParlor={defaultParlor} />
    </>
  );
}

interface ParlorFlowerContentSectionProps {
  defaultParlor: ParlorDetailProps;
}

const ParlorFlowerContentSection = ({
  defaultParlor,
}: ParlorFlowerContentSectionProps) => {
  const excludeParlorPage = excludeParlorList.includes(
    String(defaultParlor.id)
  );

  if (excludeParlorPage) {
    return <Stack>해당 장례식장 요청으로 정보를 공개하지 않습니다.</Stack>;
  }

  return (
    <>
      <ATypeButtonLinks
        defaultParlor={defaultParlor}
        event={{
          domain: "dggb",
          path: "funeral_place_flower",
          trigger_and_target: "uv_funeral_place",
        }}
      />

      <DTypeTabs
        href={`funeral-places/${defaultParlor.id}`}
        mainColor={regionColor}
        selectTab="화환 보내기"
        defaultParlor={defaultParlor}
        event={{
          domain: "dggb",
          path: "funeral_place_flower",
          trigger_and_target: "DType_tab",
        }}
      />

      <Stack padding="20px 0 50px" style={{ background: regionColor.c100 }}>
        <ATypeFlowerSummary
          defaultParlor={defaultParlor}
          event={{
            domain: "dggb",
            path: "funeral_place_flower",
            trigger_and_target: "uv_wreath_req",
          }}
        />
      </Stack>
      <Stack padding="20px 0px">
        <ATypeParlorList
          sido={defaultParlor.sido}
          sigungu={defaultParlor.sigungu}
          title="주변 장례식장"
          CardViewType={(res) => (
            <ATypeCardView
              key={res.companyname}
              name={res.companyname}
              href={`/funeral-places/${defaultParlor.id}/price/`}
              descriptions={[
                `빈소 평균 비용 : ${Number(
                  res.binso_average_charge
                ).toLocaleString()}원`,
              ]}
              imgSrc={res.images[0] ? res.images[0].url : ""}
              event={{
                domain: "dggb",
                path: "funeral_place_flower",
                trigger_and_target: "uv_prlr_detail",
              }}
            />
          )}
        />
      </Stack>
    </>
  );
};
